'use client';
import { memo, useCallback, useMemo } from 'react';
import { SvgChart } from '../lib/components';
import { Axes, DayShades, Grid } from '../lib/components/Graph';
import { AreaStepped } from '../lib/components/Shapes/Area';
import { LineStepped } from '../lib/components/Shapes/Line';
import { useExtent, useScaleLinearY, useScaleTimeX } from '../lib/hooks/d3';

const useCulmulate = (data: any, dataKey: any, accKey: any) =>
  useMemo(
    () =>
      data.reduce(
        (acc: any, d: any, i: any) =>
          acc.concat({ ...d, [accKey]: ((i && acc[i - 1][accKey]) || 0) + (d[dataKey] || 1) }),
        [],
      ),
    [data, accKey, dataKey],
  );

const TimeAreaChart = memo(({ data, markers = [], utcOffset, dataKey, color }: any) => {
  const keyX = 'date';
  const keyY = 'acc';
  const cumulated = useCulmulate(data, dataKey, keyY);
  const first = cumulated[0];
  const last = cumulated[cumulated.length - 1];
  const maxValue = (last && last[keyY]) || 1;
  const now = new Date();

  const [minDate, maxDate] = useExtent([first, last, ...markers], (d: any) => d && d[keyX]);

  const scaleX = useScaleTimeX(minDate, maxDate, utcOffset);
  const scaleY = useScaleLinearY(0, maxValue);

  // increase shown area ...
  const extended = cumulated.slice();

  // ... on the left side with the first marker or first date on 0
  if (first && minDate) {
    const graphStartDate = first[keyX] < minDate ? first[keyX] : minDate;
    extended.unshift({ [keyX]: graphStartDate, [keyY]: 0 });
  }

  // ... on the right side up to now or the max domain
  const [, axisEndDate] = scaleX.domain();
  const graphEndDate = axisEndDate < now ? axisEndDate : now;
  if (last && graphEndDate > last[keyY]) {
    extended.push({ [keyX]: graphEndDate, [keyY]: last[keyY] || 0 });
  }

  const getHoveredTick = useCallback((x: any) => scaleX.interval.floor(scaleX.invert(x)), [scaleX]);

  return (
    <SvgChart getHoveredTick={getHoveredTick}>
      <DayShades scaleX={scaleX} scaleY={scaleY} data={cumulated} />
      <Grid scaleX={scaleX} scaleY={scaleY} />
      <AreaStepped
        data={extended}
        keyX={keyX}
        keyY1={keyY}
        scaleX={scaleX}
        scaleY={scaleY}
        color={color}
      />
      <Axes data={cumulated} scaleX={scaleX} scaleY={scaleY} drawYAxis />
      <LineStepped
        data={extended}
        keyX={keyX}
        keyY={keyY}
        scaleX={scaleX}
        scaleY={scaleY}
        stroke={color}
      />
    </SvgChart>
  );
});

export default TimeAreaChart;
